/**
 * Bridges Clerk's `useUser()` into a plain React context that `SessionProvider` can read
 * without caring whether Clerk is mounted at all.
 *
 * Why a bridge instead of calling `useUser()` straight from `SessionProvider`: `useUser()`
 * throws outside a `<ClerkProvider>` ancestor, and `main.tsx` only mounts `<ClerkProvider>`
 * when `VITE_CLERK_PUBLISHABLE_KEY` is set (see `lib/auth.ts`'s `resolveClerkAuthMode`) — the
 * no-key fallback must keep working with zero Clerk components in the tree. Routing the value
 * through `ClerkIdentityContext` means `SessionProvider` can call one unconditional
 * `useContext` and get `null` for free when there's no `ClerkIdentityBridge` (and hence no
 * `ClerkProvider`) above it, instead of branching on which hook to call — which would violate
 * the rules of hooks the moment that branch could differ across renders.
 *
 * This is identity only — see `lib/auth.ts`'s header for what a Clerk session does and does
 * not authorise.
 */

import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useAuth, useClerk, useUser } from '@clerk/clerk-react'
import { deriveClerkIdentity, type ClerkIdentity } from '../lib/auth'
import { setClerkTokenSource } from '../lib/api/frappeSession'
import { registerCompanyPersister, restoreCompany } from '../lib/api/company'
import { registerMailboxPersister, restoreMyMailbox } from '../lib/api/mailbox'

/** Where the purchaser's operating company lives on their Clerk user — see `company.ts`. */
const COMPANY_KEY = 'bosun_company'
/** "My mailbox" (2026-09-18) — persisted beside the company, same field, same reasoning. */
const MAILBOX_KEY = 'bosun_mailbox'

const ClerkIdentityContext = createContext<ClerkIdentity | null>(null)

/**
 * Clerk's `signOut`, or `null` when Clerk isn't mounted — carried through the SAME context for the
 * same reason as the identity above.
 *
 * `Layout` used to call `useClerk()` directly for its sign-out button. That hook throws outside a
 * `<ClerkProvider>`, and the keyless path renders the whole app WITHOUT one, so every route in the
 * app crashed with "useClerk can only be used within the <ClerkProvider> component" — the exact
 * failure this bridge's header describes, reintroduced one component over.
 */
const ClerkSignOutContext = createContext<(() => Promise<void>) | null>(null)

/** Only ever mounted inside `<ClerkProvider>` — see `main.tsx`. */
export function ClerkIdentityBridge({ children }: { children: ReactNode }) {
  const { user } = useUser()
  const { signOut } = useClerk()
  const { getToken } = useAuth()

  /**
   * Install the token source DURING RENDER, not in an effect, and here rather than in
   * `FrappeSessionGate`.
   *
   * Here, because the gate is not mounted in the deployed (proxy) architecture —
   * `VITE_BOSUN_SESSION_BRIDGE=0` skips it — and `currentClerkToken` is what every transport now
   * reads to authenticate against `server/index.ts`. Left in the gate, production would install it
   * never and every request would 401.
   *
   * During render, because an effect runs AFTER children mount, and children fire React Query
   * requests on mount. That ordering would send the app's first burst of requests with no
   * `Authorization` header — a fleet of spurious 401s on every page load, self-healing on refetch
   * and therefore intermittent, which is the worst shape for a bug like this. Assigning a module
   * variable is idempotent and safe to repeat.
   */
  setClerkTokenSource(() => getToken())

  const identity = useMemo(() => deriveClerkIdentity(user), [user])
  const doSignOut = useMemo(() => () => signOut().then(() => undefined), [signOut])

  /*
    The operating company, per person, across refreshes and devices (Akhil, 2026-09-16): read
    off the Clerk user's `unsafeMetadata` once it has loaded, and written back whenever
    `setCompany` is called anywhere in the app. `unsafeMetadata` is the field a user may write
    from the browser; it carries a company NAME, nothing sensitive, and the proxy never reads it.
  */
  useEffect(() => {
    if (!user) return
    const stored = user.unsafeMetadata?.[COMPANY_KEY]
    if (typeof stored === 'string') restoreCompany(stored)
    registerCompanyPersister((company) => {
      if (user.unsafeMetadata?.[COMPANY_KEY] === company) return
      void user.update({ unsafeMetadata: { ...user.unsafeMetadata, [COMPANY_KEY]: company } }).catch(() => {
        // The session keeps the value either way; only the memory across refreshes is lost.
      })
    })
    const storedMailbox = user.unsafeMetadata?.[MAILBOX_KEY]
    if (typeof storedMailbox === 'string') restoreMyMailbox(storedMailbox)
    registerMailboxPersister((address) => {
      if (user.unsafeMetadata?.[MAILBOX_KEY] === address) return
      void user.update({ unsafeMetadata: { ...user.unsafeMetadata, [MAILBOX_KEY]: address } }).catch(() => {
        // The session keeps the value either way; only the memory across refreshes is lost.
      })
    })
    return () => {
      registerCompanyPersister(null)
      registerMailboxPersister(null)
    }
  }, [user])
  return (
    <ClerkIdentityContext.Provider value={identity}>
      <ClerkSignOutContext.Provider value={doSignOut}>{children}</ClerkSignOutContext.Provider>
    </ClerkIdentityContext.Provider>
  )
}

/**
 * Clerk's sign-out, or `null` when Clerk is not configured. Safe to call unconditionally from
 * anywhere; a caller that gets `null` has nothing to sign out of and should hide the control.
 */
export function useClerkSignOut(): (() => Promise<void>) | null {
  return useContext(ClerkSignOutContext)
}

/**
 * The Clerk-sourced identity, or `null` when Clerk isn't configured (no bridge above), the
 * user hasn't loaded yet, or they have no usable primary email. Safe to call unconditionally
 * from anywhere — the default context value covers the "no Clerk" case.
 */
export function useClerkIdentity(): ClerkIdentity | null {
  return useContext(ClerkIdentityContext)
}
